import { useContextMenu } from '@/common/contextMenu/useContextMenu';
import { useEntityChangeSubscr } from '@/common/entityChange/EntityChangeContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Renders an Entity's rows as expandable cards instead of a grid. Fetches like EntityList (augRowsReq +
 * entity-change subscription) and leaves the presentation of each row to the caller.
 *
 * - renderSummary(row): always-visible header content
 * - renderBody(row): content revealed on expand (omit for non-expandable cards)
 * - renderActions(row): buttons rendered at the bottom of the expanded card
 * - sortOptions: [{ label, compare(a, b) }] offered in the Sort selector
 * - groupByOptions: [{ label, key(row) }] offered in the "Group by" selector
 * - contextMenuItems(row): [{ name, icon, action, disabled }] shown on right-click, same pattern as grid rows
 * - highlightId: row id to expand, scroll to, and briefly emphasize (e.g. after a save)
 * - hideSort: suppresses the "Sort by" control entirely (sortRows/sortOptions still determine the fixed order)
 */
export const EntityCardList = ({
  module,
  entity,
  augRowsReq,
  refreshTrigger: refreshTriggerProp,
  sortRows,
  renderSummary,
  renderBody,
  renderActions,
  sortOptions = [],
  defaultSort = 'Recently changed',
  groupByOptions = [],
  contextMenuItems,
  headerButtons,
  title,
  emptyState,
  highlightId,
  hideSort = false,
}) => {
  const rsrc = useRsrc();
  const theme = useTheme();
  const { openContextMenu, contextMenu } = useContextMenu();

  const [rows, setRows] = useState(null);
  const [sortBy, setSortBy] = useState(defaultSort);
  const [groupBy, setGroupBy] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [emphasizedId, setEmphasizedId] = useState(null);
  const cardRefs = useRef({});

  const { refreshTrigger } = useEntityChangeSubscr(module, entity);

  useEffect(() => {
    const rowsReq = {};
    augRowsReq?.(rowsReq);
    rsrc.rows(module, entity, rowsReq).then((res) => {
      setRows(res ?? []);
    });
  }, [refreshTrigger, refreshTriggerProp, augRowsReq]);

  // Expand, scroll to, and emphasize the highlighted card once its row is present
  useEffect(() => {
    if (highlightId == null || !rows?.some((row) => row.id === highlightId)) return;
    setExpandedIds((prev) => new Set(prev).add(highlightId));
    setEmphasizedId(highlightId);
    const timer = setTimeout(() => setEmphasizedId(null), 2500);
    requestAnimationFrame(() => cardRefs.current[highlightId]?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    return () => clearTimeout(timer);
  }, [highlightId, rows]);

  const sortedRows = useMemo(() => {
    if (!rows) return [];
    const option = sortOptions.find((candidate) => candidate.label === sortBy);
    const compare = option?.compare ?? sortRows ?? recentlyChanged;
    return [...rows].sort(compare);
  }, [rows, sortBy, sortOptions, sortRows]);

  const groups = useMemo(() => {
    const option = groupByOptions.find((opt) => opt.label === groupBy);
    if (!option) return [{ label: null, rows: sortedRows }];
    const labelToRows = new Map();
    sortedRows.forEach((row) => {
      const key = option.key(row) ?? '—';
      if (!labelToRows.has(key)) labelToRows.set(key, []);
      labelToRows.get(key).push(row);
    });
    return [...labelToRows.entries()]
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([label, groupRows]) => ({ label, rows: groupRows }));
  }, [sortedRows, groupBy, groupByOptions]);

  const toggle = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedIds(new Set(rows.map((row) => row.id)));
  const collapseAll = () => setExpandedIds(new Set());

  if (rows == null) return null;

  const isEmpty = rows.length === 0;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
        {title && (
          <Typography variant="h6" sx={{ mr: 1 }}>
            {title} ({rows.length})
          </Typography>
        )}
        {!isEmpty && !hideSort && (
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Sort by</InputLabel>
            <Select label="Sort by" value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              <MenuItem value="Recently changed">Recently changed</MenuItem>
              {sortOptions.map((option) => (
                <MenuItem key={option.label} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {!isEmpty && groupByOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Group by</InputLabel>
            <Select label="Group by" value={groupBy} onChange={(event) => setGroupBy(event.target.value)}>
              <MenuItem value="">None</MenuItem>
              {groupByOptions.map((opt) => (
                <MenuItem key={opt.label} value={opt.label}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
          {!isEmpty && renderBody && (
            <>
              <Tooltip title="Expand all">
                <IconButton size="small" onClick={expandAll}>
                  <FontAwesomeIcon icon={fas.faChevronDown} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Collapse all">
                <IconButton size="small" onClick={collapseAll}>
                  <FontAwesomeIcon icon={fas.faChevronUp} />
                </IconButton>
              </Tooltip>
            </>
          )}
          {headerButtons}
        </Stack>
      </Stack>

      {isEmpty && emptyState && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          {emptyState}
        </Paper>
      )}

      {groups.map((group) => (
        <Box key={group.label ?? '__all'}>
          {group.label != null && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
              {group.label} ({group.rows.length})
            </Typography>
          )}
          <Stack spacing={1}>
            {group.rows.map((row) => {
              const expandable = Boolean(renderBody);
              const expanded = expandable && expandedIds.has(row.id);
              const emphasized = emphasizedId === row.id;
              return (
                <Accordion
                  key={row.id}
                  ref={(el) => (cardRefs.current[row.id] = el)}
                  expanded={expanded}
                  onChange={() => expandable && toggle(row.id)}
                  disableGutters
                  variant="outlined"
                  sx={{
                    '&:before': { display: 'none' },
                    transition: 'border-color 300ms, box-shadow 300ms',
                    ...(emphasized && {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
                    }),
                  }}
                >
                  <AccordionSummary
                    expandIcon={expandable ? <FontAwesomeIcon icon={fas.faChevronDown} /> : null}
                    onContextMenu={
                      contextMenuItems ? (event) => openContextMenu(event, contextMenuItems(row)) : undefined
                    }
                    sx={{ '& .MuiAccordionSummary-content': { minWidth: 0, alignItems: 'center' } }}
                  >
                    {renderSummary(row, { expanded })}
                  </AccordionSummary>
                  {expandable && <AccordionDetails>{expanded && renderBody(row)}</AccordionDetails>}
                  {expandable && renderActions && <AccordionActions>{renderActions(row)}</AccordionActions>}
                </Accordion>
              );
            })}
          </Stack>
        </Box>
      ))}
      {contextMenu}
    </Stack>
  );
};

const recentlyChanged = (a, b) => {
  const aInstant = a.recInstant ?? a.verStartInstant ?? '';
  const bInstant = b.recInstant ?? b.verStartInstant ?? '';
  return String(bInstant).localeCompare(String(aInstant)) || (b.id ?? 0) - (a.id ?? 0);
};
